import { Link } from 'react-router-dom';
import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ImageSlot from '../components/ui/ImageSlot';
import { BLOG_POSTS } from '../data/blogPosts';
import saleemPhoto from '../assets/images/saleem.png';

/** Blog post (article) page — Malta rollout as the reference content. */
export default function BlogPost() {
  const post = BLOG_POSTS[0];

  return (
    <div className="bg-white">
      <UtilityBar />
      <Nav variant="light" />

      <div className="sticky top-0 z-10" style={{ height: 3, background: 'var(--line)' }}>
        <div style={{ width: '32%', height: '100%', background: 'var(--yellow)' }} />
      </div>

      <article style={{ padding: '48px 0 0' }}>
        <div className="lc-container" style={{ maxWidth: 880 }}>
          <div className="flex items-center gap-2 mb-8" style={{ fontSize: 13, color: 'var(--muted)' }}>
            <Link to="/blog" style={{ color: 'var(--muted)' }}>Blog</Link>
            <span>›</span>
            <a href="#" style={{ color: 'var(--muted)' }}>Case Study</a>
            <span>›</span>
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Malta National Rollout</span>
          </div>

          <div className="flex items-center gap-2 mb-4.5">
            <span className="font-bold tracking-wider" style={{ fontSize: 11, padding: '5px 10px', borderRadius: 999, background: 'rgba(43,108,217,0.12)', color: 'var(--blue)' }}>CASE STUDY</span>
            <span className="font-semibold" style={{ fontSize: 11, padding: '5px 10px', borderRadius: 999, background: 'var(--bg-soft)', color: 'var(--ink-2)' }}>🇲🇹 Malta</span>
            <span className="font-semibold" style={{ fontSize: 11, padding: '5px 10px', borderRadius: 999, background: 'var(--bg-soft)', color: 'var(--ink-2)' }}>Deployment</span>
          </div>

          <h1 className="mb-5.5" style={{ fontSize: 60, letterSpacing: '-0.03em', lineHeight: 1.05 }}>
            How we rolled Lincoln out to <em className="italic" style={{ color: 'var(--blue)' }}>every</em> Maltese high school in 12 months
          </h1>

          <p className="mb-8 leading-snug font-normal" style={{ fontSize: 22, color: 'var(--ink-2)' }}>
            What it took to deploy a voice-AI tutor into 47 schools across an entire country — and what we learned in the process.
          </p>

          <div className="flex justify-between items-center pt-6 pb-6 flex-wrap gap-4"
            style={{ borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
            <div className="flex items-center gap-3">
              <ImageSlot id="post-author-saleem" style={{ width: 52, height: 52 }} shape="circle" src={saleemPhoto} placeholder="" />
              <div>
                <div className="font-bold" style={{ fontSize: 15 }}>{post.author}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{post.author_role} · {post.date} · {post.read} read</div>
              </div>
            </div>
            <ShareButtons />
          </div>
        </div>

        <div className="lc-container" style={{ maxWidth: 1080, marginTop: 40, marginBottom: 56 }}>
          <div className="relative overflow-hidden" style={{ borderRadius: 20, aspectRatio: '16/9' }}>
            <ImageSlot
              id="post-hero-malta"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              shape="rounded"
              radius="20"
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1400&auto=format&fit=crop&q=80"
              placeholder="Drop article hero photo — Maltese classroom or students with Lincoln"
            />
          </div>
          <div className="text-center mt-2.5 italic" style={{ fontSize: 12, color: 'var(--muted)' }}>
            St. Aloysius College, Birkirkara — students in their AI Literacy lesson, March 2026.
          </div>
        </div>

        <div className="lc-container">
          <div className="grid" style={{ gridTemplateColumns: '220px 1fr 60px', gap: 56, maxWidth: 1080, margin: '0 auto' }}>
            <aside>
              <div className="sticky" style={{ top: 32 }}>
                <div className="font-bold tracking-widest mb-3.5" style={{ fontSize: 11, color: 'var(--muted)' }}>ON THIS PAGE</div>
                <ul className="list-none p-0 m-0 flex flex-col gap-1" style={{ borderLeft: '2px solid var(--line)' }}>
                  {['The brief', 'Why voice-first', 'Co-designing the curriculum', 'The pilot', 'Going national', 'What we learned', "What's next"].map((s, i) => (
                    <li key={s}>
                      <a href="#" className="block"
                        style={{
                          padding: '6px 14px',
                          marginLeft: -2,
                          borderLeft: i === 2 ? '2px solid var(--yellow)' : '2px solid transparent',
                          fontSize: 13,
                          fontWeight: i === 2 ? 700 : 500,
                          color: i === 2 ? 'var(--ink)' : 'var(--muted)',
                        }}>{s}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Article body */}
            <div className="text-ink-2" style={{ fontSize: 18, lineHeight: 1.75 }}>
              <p className="font-serif" style={{ fontSize: 22, lineHeight: 1.55, color: 'var(--ink)', fontWeight: 400 }}>
                In 2023, the Ministry of Education of Malta gave us a brief that sounded impossible: every secondary student in the country, AI-literate by 2027. We had 12 months to roll out the first cohort. This is how we did it.
              </p>

              <h2 style={{ marginTop: 48, fontSize: 34, color: 'var(--ink)', letterSpacing: '-0.02em' }}>The brief</h2>
              <p className="mt-4">
                Malta is small — 500,000 people on an island in the Mediterranean — but ambitious. The MOE didn&apos;t just want AI lessons. They wanted every student to <strong>actually be able to use AI tools well</strong>, in Maltese and English, with measurable lift on assessment. Traditional curriculum vendors quoted 18–24 months for materials alone. By the time their content shipped, the AI landscape would have shifted twice.
              </p>

              <blockquote className="rounded-r-2xl"
                style={{ margin: '40px 0', padding: '28px 32px', background: 'linear-gradient(135deg, rgba(43,108,217,0.06), rgba(240,184,12,0.04))', borderLeft: '4px solid var(--yellow)' }}>
                <div className="font-serif font-medium leading-tight" style={{ fontSize: 26, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
                  &ldquo;We needed a partner who could move at the speed of AI, not the speed of textbooks.&rdquo;
                </div>
                <div className="mt-3.5" style={{ fontSize: 13, color: 'var(--muted)' }}>
                  — Hon. Clifton Grima, Minister of Education
                </div>
              </blockquote>

              <h2 style={{ marginTop: 48, fontSize: 34, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Why voice-first</h2>
              <p className="mt-4">
                Most online courses fail with 11–18-year-olds for the same reason: reading-heavy interfaces compete with TikTok, Instagram, and YouTube. We didn&apos;t have a chance unless the experience felt at least as natural as the things competing for the same attention budget.
              </p>
              <p className="mt-4">
                Voice solved this. Students put on headphones, walk around the classroom, talk to Lincoln in either Maltese or English, and he talks back. The cognitive load drops; engagement spikes; teachers reclaim their time to do what AI can&apos;t — connect concepts to real life.
              </p>

              <div className="overflow-hidden relative" style={{ margin: '40px 0', borderRadius: 16 }}>
                <ImageSlot
                  id="post-inline-1"
                  style={{ width: '100%', height: 360 }}
                  shape="rounded"
                  radius="16"
                  src="https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1200&auto=format&fit=crop&q=80"
                  placeholder="Drop in-article photo: students using Lincoln in pairs"
                />
              </div>

              <h2 style={{ marginTop: 48, fontSize: 34, color: 'var(--ink)', letterSpacing: '-0.02em' }}>Co-designing the curriculum</h2>
              <p className="mt-4">
                In September 2024, we ran a three-week workshop with 12 Maltese teachers — heads of science, ICT coordinators, and pedagogy specialists. The output: 24 lessons, each 25 minutes long, mapped to the existing national framework. We didn&apos;t replace anything teachers were doing; we replaced the parts they hated.
              </p>

              <div style={{ margin: '32px 0', padding: '24px 28px', background: 'var(--bg-cream)', borderRadius: 14, border: '1px solid var(--line)' }}>
                <div className="font-bold tracking-widest mb-2.5" style={{ fontSize: 11, color: 'var(--blue)' }}>KEY DECISIONS</div>
                <ul className="list-none p-0 m-0 flex flex-col gap-2.5">
                  {[
                    'Lessons sized to fit one school period (25 min)',
                    'Mother-tongue (Maltese) first, English second',
                    'Quizzes after every lesson — non-negotiable',
                    'Teachers see a class dashboard, not student-level data',
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5" style={{ fontSize: 15, color: 'var(--ink-2)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="3" style={{ marginTop: 3, flexShrink: 0 }}><polyline points="20,6 9,17 4,12" /></svg>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              <h2 style={{ marginTop: 48, fontSize: 34, color: 'var(--ink)', letterSpacing: '-0.02em' }}>The pilot</h2>
              <p className="mt-4">
                January 2025 — we went live in 6 schools with 1,400 students. The first two weeks were rough. We discovered that Lincoln&apos;s Maltese was technically correct but felt foreign — too formal, no slang. So we retrained the voice model with 80 hours of recorded conversations from 14 native speakers, and the engagement numbers jumped overnight.
              </p>

              <div className="grid" style={{ margin: '32px 0', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                {[
                  { v: '12,481', l: 'students enrolled' },
                  { v: '8,200',  l: 'certificates issued' },
                  { v: '+47',    l: 'points lift on AI assessment' },
                ].map((s) => (
                  <div key={s.l} className="bg-white" style={{ padding: 20, borderRadius: 12, border: '1px solid var(--line)' }}>
                    <div className="font-serif font-semibold leading-none" style={{ fontSize: 36, color: 'var(--ink)', letterSpacing: '-0.02em' }}>{s.v}</div>
                    <div className="mt-2" style={{ fontSize: 13, color: 'var(--muted)' }}>{s.l}</div>
                  </div>
                ))}
              </div>

              <h2 style={{ marginTop: 48, fontSize: 34, color: 'var(--ink)', letterSpacing: '-0.02em' }}>What we learned</h2>
              <p className="mt-4">Three lessons we&apos;ll carry into every future deployment:</p>
              <ol className="mt-3.5 pl-6 flex flex-col gap-3 leading-loose" style={{ fontSize: 17, color: 'var(--ink-2)' }}>
                <li><strong style={{ color: 'var(--ink)' }}>Mother tongue is non-negotiable.</strong> Students will not be vulnerable enough to learn in a second language.</li>
                <li><strong style={{ color: 'var(--ink)' }}>Teachers are partners, not obstacles.</strong> Co-design beats &ldquo;rollout&rdquo; every time.</li>
                <li><strong style={{ color: 'var(--ink)' }}>Ship the rough version. Iterate weekly.</strong> The polished MVP that takes 18 months is the wrong shape.</li>
              </ol>

              <div className="mt-9 text-white flex justify-between items-center gap-6"
                style={{ padding: 24, background: 'var(--navy)', borderRadius: 16 }}>
                <div>
                  <div className="font-serif font-semibold text-white" style={{ fontSize: 22 }}>Want this in your country?</div>
                  <div className="mt-1" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>30-minute call. No commitment.</div>
                </div>
                <Link to="/providers" className="lc-btn yellow">Book a call →</Link>
              </div>
            </div>

            {/* Share rail — desktop sticky right column. Mobile uses the
                fixed floating rail rendered below. */}
            <aside className="hidden lg:block">
              <div className="sticky flex flex-col items-center gap-2" style={{ top: 32 }}>
                <div className="font-bold tracking-widest mb-2"
                  style={{ fontSize: 9, color: 'var(--muted)', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>SHARE</div>
                {['𝕏', 'in', 'f', '✉', '🔗'].map((icon, i) => (
                  <button key={i} type="button" className="bg-white rounded-full font-bold"
                    style={{ width: 40, height: 40, border: '1px solid var(--line)', color: 'var(--ink)', fontSize: 14 }}>{icon}</button>
                ))}
                <div className="mt-3 pt-2 w-full text-center" style={{ borderTop: '1px solid var(--line)' }}>
                  <div className="font-semibold" style={{ fontSize: 11, color: 'var(--muted)' }}>💬</div>
                  <div className="font-bold mt-0.5" style={{ fontSize: 11, color: 'var(--ink)' }}>42</div>
                </div>
                <div className="w-full text-center" style={{ padding: '4px 0' }}>
                  <div className="font-semibold" style={{ fontSize: 11, color: 'var(--muted)' }}>♥</div>
                  <div className="font-bold mt-0.5" style={{ fontSize: 11, color: 'var(--ink)' }}>318</div>
                </div>
              </div>
            </aside>
          </div>

          {/* Mobile share rail — fixed to the right edge, vertically centered */}
          <div className="lg:hidden flex flex-col gap-2 bg-white rounded-full"
            style={{ position: 'fixed', right: 8, top: '50%', transform: 'translateY(-50%)', padding: 6, border: '1px solid var(--line)', boxShadow: 'var(--shadow-md)', zIndex: 20 }}>
            {['𝕏', 'in', 'f', '✉', '🔗'].map((icon, i) => (
              <button key={i} type="button" className="bg-white rounded-full font-bold"
                style={{ width: 36, height: 36, border: '1px solid var(--line)', color: 'var(--ink)', fontSize: 13 }}>{icon}</button>
            ))}
          </div>
        </div>
      </article>

      {/* Author bio */}
      <section style={{ background: 'var(--bg-cream)', padding: '60px 0', marginTop: 80 }}>
        <div className="lc-container" style={{ maxWidth: 880 }}>
          <div className="grid items-center" style={{ gridTemplateColumns: '100px 1fr', gap: 24 }}>
            <ImageSlot id="bio-saleem" style={{ width: 100, height: 100 }} shape="circle" src={saleemPhoto} placeholder="" />
            <div>
              <div className="font-bold tracking-widest" style={{ fontSize: 11, color: 'var(--muted)' }}>AUTHOR</div>
              <h3 className="mt-1" style={{ fontSize: 28 }}>Saleem Qureshi</h3>
              <div className="font-bold mt-1" style={{ fontSize: 13, color: 'var(--blue)' }}>Co-founder &amp; CEO, Lincoln School</div>
              <p className="mt-3 leading-relaxed" style={{ fontSize: 15, color: 'var(--ink-2)' }}>
                Saleem founded Lincoln Method in 2008 in Karachi and rebuilt it as Lincoln School in 2024 around voice AI. He&apos;s deployed Lincoln in 10 countries across ministries, NGOs, and tutoring agencies.
              </p>
              <div className="mt-3.5 flex gap-3.5" style={{ fontSize: 13 }}>
                <a href="#" className="lc-link">More from Saleem →</a>
                <a href="#" style={{ color: 'var(--muted)' }}>LinkedIn ↗</a>
                <a href="#" style={{ color: 'var(--muted)' }}>X / Twitter ↗</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comments */}
      <section style={{ padding: '60px 0' }}>
        <div className="lc-container" style={{ maxWidth: 880 }}>
          <div className="flex justify-between items-center mb-7">
            <h2 style={{ fontSize: 32 }}>Comments <span className="font-medium" style={{ color: 'var(--muted)' }}>(42)</span></h2>
            <select className="bg-white" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit' }}>
              <option>Newest first</option><option>Most liked</option>
            </select>
          </div>

          <div className="grid mb-9" style={{ gridTemplateColumns: '44px 1fr', gap: 14, padding: 18, background: 'var(--bg-cream)', borderRadius: 14 }}>
            <ImageSlot id="comment-me" style={{ width: 44, height: 44 }} shape="circle" src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&auto=format&fit=crop&q=80" placeholder="" />
            <div>
              <textarea
                placeholder="Share your thoughts on this story…"
                className="w-full outline-none"
                style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', minHeight: 90, boxSizing: 'border-box' }}
              />
              <div className="flex justify-between items-center mt-2">
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Markdown supported · Be kind</span>
                <button type="button" className="lc-btn primary sm">Post comment</button>
              </div>
            </div>
          </div>

          {[
            { name: 'Maria Cassar',  role: 'Head of Science, St. Aloysius College',     time: '2 days ago', text: "We've seen exactly this in our classroom. The shift to mother-tongue voice was a game-changer for engagement, especially with our lower-attainment students. Great read.", likes: 38, replies: 3,
              photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&auto=format&fit=crop&q=80' },
            { name: 'Tomi Vainio',   role: 'Principal, Helsinki',                       time: '3 days ago', text: "Curious about your support team size when going national. 8 people for 47 schools seems lean — was that enough or did you wish you'd staffed more?", likes: 12, replies: 1,
              photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80' },
            { name: 'Sarah Levin',   role: 'Curriculum specialist',                     time: '4 days ago', text: 'The point about co-design beating rollout is huge. Most ed-tech vendors still treat teachers as installation targets, not partners.', likes: 24, replies: 0,
              photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&auto=format&fit=crop&q=80' },
          ].map((c) => (
            <div key={c.name} className="grid mb-6 pb-6" style={{ gridTemplateColumns: '44px 1fr', gap: 14, borderBottom: '1px solid var(--line)' }}>
              <ImageSlot id={`comment-${c.name.replace(/\s/g, '-')}`} style={{ width: 44, height: 44 }} shape="circle" src={c.photo} placeholder="" />
              <div>
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="font-bold" style={{ fontSize: 14 }}>{c.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{c.role}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>· {c.time}</span>
                </div>
                <p className="leading-relaxed" style={{ fontSize: 15, color: 'var(--ink-2)' }}>{c.text}</p>
                <div className="mt-3 flex items-center gap-4.5" style={{ fontSize: 13 }}>
                  <button type="button" className="font-semibold inline-flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>♥ {c.likes}</button>
                  <button type="button" className="font-semibold" style={{ color: 'var(--muted)' }}>Reply</button>
                  {c.replies > 0 && <span className="font-semibold" style={{ color: 'var(--blue)' }}>· {c.replies} replies</span>}
                  <button type="button" className="ml-auto" style={{ color: 'var(--muted)' }}>⋯</button>
                </div>
              </div>
            </div>
          ))}

          <div className="text-center mt-6">
            <button type="button" className="lc-btn ghost">Load 39 more comments →</button>
          </div>
        </div>
      </section>

      {/* Related */}
      <section style={{ background: 'var(--bg-cream)', padding: '80px 0' }}>
        <div className="lc-container">
          <div className="flex justify-between items-end mb-8">
            <div>
              <span className="lc-eyebrow">Related stories</span>
              <h2 className="mt-3" style={{ fontSize: 32 }}>More from the work.</h2>
            </div>
            <Link to="/blog" className="lc-link" style={{ color: 'var(--ink-2)' }}>All stories →</Link>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {BLOG_POSTS.slice(1, 4).map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="lc-card overflow-hidden flex flex-col">
                <div className="relative" style={{ height: 160, background: 'var(--bg-soft)' }}>
                  <ImageSlot id={`related-${p.slug}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} shape="rect" src={p.photo} placeholder={p.tag} />
                </div>
                <div className="flex flex-col flex-1" style={{ padding: 20 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold tracking-wider" style={{ fontSize: 10, color: p.cat_color }}>{p.category.toUpperCase()}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>· {p.read}</span>
                  </div>
                  <h3 className="flex-1" style={{ fontSize: 19, lineHeight: 1.25 }}>{p.title}</h3>
                  <div className="mt-4 flex items-center gap-2" style={{ fontSize: 12, color: 'var(--muted)' }}>
                    <ImageSlot id={`rel-author-${p.author.replace(/\s/g, '-')}`} style={{ width: 22, height: 22 }} shape="circle" src={p.authorPhoto} placeholder="" />
                    <span className="font-semibold" style={{ color: 'var(--ink-2)' }}>{p.author}</span>
                    <span>· {p.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ShareButtons() {
  return (
    <div className="flex gap-2">
      {[
        { l: 'Tweet', icon: '𝕏',  bg: '#000',         color: '#fff' },
        { l: 'Share', icon: 'in', bg: '#0a66c2',      color: '#fff' },
        { l: 'Post',  icon: 'f',  bg: '#1877f2',      color: '#fff' },
        { l: 'Copy',  icon: '🔗', bg: 'var(--bg-soft)', color: 'var(--ink)' },
      ].map((s) => (
        <button key={s.l} type="button" className="inline-flex items-center gap-1.5 rounded-full font-semibold"
          style={{ padding: '8px 14px', fontSize: 13, background: s.bg, color: s.color }}>
          <span className="font-bold" style={{ fontSize: 14 }}>{s.icon}</span>{s.l}
        </button>
      ))}
    </div>
  );
}
